import { activeRightStack, store } from '@/app/store';
import { hidePopup } from '@/components/PopupContainer/popupService';
import {
   getSelectedMessages,
   resetSelectedMessages,
   updateMediaStatus,
   upsertRecentChatItem,
} from '@/features/chats/model';
import { dispatchMessagesToConversation } from '@/features/chats/services/messageDispatchService';
import { patchBufferedMessage } from '@/features/chats/services/messageBuffer';
import { formatForwardMessages, formatRecentChat } from '@/features/chats/utils/messageFormatter';
import { sdkForwardMessagesToMultipleUsers } from '@/infrastructure/sdk';
import { i18n } from '@/localization/i18n';
import { blockOfflineAction, getUserIdFromJid, logger, showErrorToast } from '@/shared/utils';

/**
 * Apply an is_uploading change to a message's media, wherever it currently lives:
 *  - still sitting in the messageBuffer (not yet flushed to Redux), or
 *  - already flushed into state.data[chatId].messages.
 * This removes the race that previously caused the forward loader to get
 * stuck: a plain `store.dispatch(updateMediaStatus(...))` would silently
 * no-op if the buffer hadn't flushed yet, because the message didn't exist
 * in the store at that moment.
 */
const setForwardedMediaUploadStatus = (chatId, msgId, is_uploading) => {
   const patchedInBuffer = patchBufferedMessage(chatId, msgId, current => {
      if (!current?.msgBody?.media) return null;
      return {
         ...current,
         msgBody: {
            ...current.msgBody,
            media: { ...current.msgBody.media, is_uploading },
         },
      };
   });

   if (patchedInBuffer) return;

   // Not in the buffer anymore (already flushed) — update the store directly.
   store.dispatch(updateMediaStatus({ chatId, msgId, is_uploading }));
};

/**
 * Forward Messages to Multiple User/Group (Chats)
 * @param {Array} activeChatJid - Source Chat JID
 * @param {Array} toJids - Array of To Jid (Username with Domain)
 * @param {Boolean} isAsync - `true` If Forward has to be One by One - To Maintain Order
 * @returns {Object} Success/Error Response
 */
export const forwardMessagesToMultipleUsers = async (activeChatJid, toJids, isAsync = true) => {
   try {
      const isOffline = blockOfflineAction();
      if (isOffline) return;
      const activeChatId = getUserIdFromJid(activeChatJid);
      const selectedMessages = getSelectedMessages(activeChatId);
      if (!selectedMessages.length) return { status: 400, message: 'No messages selected' };

      const selectedMessagesCount = selectedMessages.length;
      // Sort chronologically (oldest first) so msgIds and newMsgIds returned
      // from formatForwardMessages stay index-aligned for the SDK call below.
      const filteredMessages = selectedMessages
         .filter(message => message.deleteStatus !== 1)
         .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
      const deleteMessagesCount = selectedMessagesCount - filteredMessages.length;

      if (selectedMessagesCount === deleteMessagesCount) {
         const message = i18n.t('toast.ALL_SELECTED_MESSAGES_DELETED');
         showErrorToast(message);
         return { status: 400, message };
      }

      if (deleteMessagesCount > 0) {
         const tKey =
            deleteMessagesCount === 1
               ? 'toast.MESSAGES_NOT_FORWARDED_DELETED_SINGULAR'
               : 'toast.MESSAGES_NOT_FORWARDED_DELETED_PLURAL';
         const message = i18n.t(tKey, { count: deleteMessagesCount });
         showErrorToast(message);
      }

      const msgIds = filteredMessages.map(m => m.msgId);
      const { newMsgIds, newMediaGroupIds, messagesByTarget } = formatForwardMessages({
         selectedMessages: filteredMessages,
         targetJids: toJids,
      });

      // Optimistic UI Update: Pre-grouped by target chat in formatForwardMessages.
      // Media messages keep whatever is_uploading value formatForwardMessages gave
      // them (the "in progress" / sending state) — the loader is meant to show here.
      Object.entries(messagesByTarget).forEach(([targetJid, messages]) => {
         const targetId = getUserIdFromJid(targetJid);
         const _messages = [...messages].reverse();
         dispatchMessagesToConversation({ chatId: targetId, messages: _messages, resetUnreadlabel: true });
         const latestMessage = messages[0];
         store.dispatch(upsertRecentChatItem(formatRecentChat(latestMessage, { changeArchiveStatus: true })));
      });

      // Clear selection and close modal after action
      hidePopup('forward_message_popup');
      store.dispatch(resetSelectedMessages({ chatId: activeChatId }));

      // Navigate to chat if only one recipient is selected
      if (toJids.length === 1) {
         const targetJid = toJids[0];
         store.dispatch(
            activeRightStack({
               activeStack: 'chats',
               activeChatId: getUserIdFromJid(targetJid),
               activeChatJid: targetJid,
            }),
         );
      }

      try {
         // Trigger SDK action with mapped IDs
         await sdkForwardMessagesToMultipleUsers(toJids, msgIds, isAsync, newMsgIds, newMediaGroupIds);

         // SDK call succeeded — mark all forwarded media as upload-complete (is_uploading -> 2).
         // Race-safe: patches the buffer if the message hasn't flushed to the store yet,
         // otherwise patches the store directly.
         Object.entries(messagesByTarget).forEach(([targetJid, messages]) => {
            const targetId = getUserIdFromJid(targetJid);
            messages.forEach(msg => {
               if (msg.msgBody?.media) {
                  setForwardedMediaUploadStatus(targetId, msg.msgId, 2);
               }
            });
         });

         return { status: 200, message: 'Forwarding triggered successfully', data: { toJids, msgIds, newMsgIds } };
      } catch (sdkError) {
         // SDK call failed — flip media back to a retryable/failed state instead of
         // leaving it stuck on the spinner forever.
         Object.entries(messagesByTarget).forEach(([targetJid, messages]) => {
            const targetId = getUserIdFromJid(targetJid);
            messages.forEach(msg => {
               if (msg.msgBody?.media) {
                  setForwardedMediaUploadStatus(targetId, msg.msgId, 0);
               }
            });
         });

         logger.error('Failed to forward messages via SDK', sdkError);
         return { status: 500, message: 'Failed to process forwarding' };
      }
   } catch (error) {
      logger.error('Failed to handle forward message', error);
      return { status: 500, message: 'Failed to process forwarding' };
   }
};
